package com.swetdot.swetgram.ui.design

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.addPathNodes
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlin.reflect.KProperty

/**
 * The Swetgram icon set: outline marks on one grid, at one stroke width, from one string each.
 *
 * WHY THIS REPLACES THE CANVAS GLYPHS. `SwetGlyph` drew seven marks with `drawLine`, `drawCircle`
 * and `drawRoundRect` at fractions of whatever size the caller passed -- `w * 0.26f`, `h * 0.62f`.
 * Three things were wrong with that and all three were visible on a phone:
 *
 *  - The proportions were eyeballed per glyph instead of laid out on a shared grid, so no two marks
 *    agreed about optical weight or margin.
 *  - The stroke was `w * 0.09f`, which is a RATIO. The same glyph at 16dp and at 24dp got 1.44dp and
 *    2.16dp of line, so a row with a 20dp overflow beside a 24dp action showed two line weights.
 *  - The vocabulary was whatever each screen needed that week: the app had THREE glyph families with
 *    three stroke constants before they were merged, and the merged set still only had seven marks,
 *    so every other icon in the app was a one-off shape drawn inline in the screen that wanted it.
 *
 * THE GRID. Every icon is authored in a 24x24 viewport with a 2.0 stroke, round caps and round
 * joins, inside the 20x20 box from (2,2) to (22,22) -- so the stroke never touches the edge and two
 * icons side by side keep the same optical margin. `ImageVector` scales the geometry AND the stroke,
 * which is the property the old ratio was trying and failing to imitate: at any one size every icon
 * on screen has exactly the same weight as every other.
 *
 * ONE SOURCE OF TRUTH PER ICON, AND IT IS AN SVG PATH STRING. `addPathNodes` parses the standard
 * `d` syntax, so what is written here is what a browser would draw from the same characters. That is
 * not a convenience: it is how this set is PROOFED. `SwetIconsTest` reads THIS FILE, checks every
 * path against the grid, and writes an HTML sheet that is rasterised and looked at -- so a mangled
 * arc is caught by eye before it reaches a screen, which is the only way a wrong curve is ever
 * caught. Authoring in the `PathBuilder` DSL instead would have made the proof and the shipped icon
 * two different pieces of code, and a proof that renders something other than what ships proves
 * nothing. Reading the source rather than the built objects is `CopyLintTest`'s trick, for the same
 * reason: the authored text is the thing under review.
 *
 * NOT AN IMPORTED SET. Compose Multiplatform ships no icon library at this version,
 * `material-icons-extended` is Android-only and frozen, and vendoring somebody else's SVGs would put
 * a licence and a build step into a module that has neither. These are authored here from the
 * conventions every good outline set shares -- a 24 grid, a 2 stroke, round terminals -- which is a
 * drawing convention rather than anybody's artwork.
 *
 * TINT COMES FROM THE CALLER, ALWAYS. The colour baked into each vector is black and is never seen:
 * [SwetIcon] draws through a tint filter. Nothing in this file reads a theme, which is what lets the
 * whole set be a plain object rather than a composable.
 */
internal object SwetIcons {

    // -- Chrome and navigation ----------------------------------------------------------------

    /** Back. A chevron and not an arrow: an arrow at 24dp on a dark bar reads as "send". */
    val ChevronLeft by stroke("M15 5 L8 12 L15 19")
    val ChevronRight by stroke("M9 5 L16 12 L9 19")
    val ChevronDown by stroke("M5 9 L12 16 L19 9")
    val ChevronUp by stroke("M5 15 L12 8 L19 15")

    val Close by stroke("M6 6 L18 18 M18 6 L6 18")
    val Plus by stroke("M12 5 L12 19 M5 12 L19 12")

    /** Swetgram Black mark supplied as `crystal.svg`; tint still comes from the active theme. */
    val Crystal by fill(
        "M21.77 9.80039 L20.97 8.00039 L19.21 4.04039 " +
            "C18.74 3.00039 18 2.15039 16.3 2.15039 H7.69995 " +
            "C5.99995 2.15039 5.25995 3.00039 4.78995 4.04039 " +
            "L3.02995 8.00039 L2.22995 9.80039 " +
            "C1.76995 10.8504 2.01995 12.3904 2.78995 13.2404 " +
            "L9.63995 20.7804 C10.94 22.2104 13.06 22.2104 14.36 20.7804 " +
            "L21.21 13.2404 C21.98 12.3904 22.23 10.8504 21.77 9.80039 Z",
    )

    val Search by stroke("M4 11 A7 7 0 0 1 18 11 A7 7 0 0 1 4 11 M16.2 16.2 L20 20")

    val Idea by stroke("M9 18 L15 18 M10 21 L14 21 M9 15 C9 12 6 12 6 8 A6 6 0 0 1 18 8 C18 12 15 12 15 15 Z")
    val Question by stroke("M12 3 A9 9 0 1 1 12 21 A9 9 0 1 1 12 3 M9 9 A3 3 0 1 1 14 11 C12 12 12 12 12 14 M12 17 L12 17.1")
    val Plan by stroke("M4 4 L20 4 L20 20 L4 20 Z M8 12 L11 15 L16 9")
    val Outcome by stroke("M4 18 L4 11 M9 18 L9 7 M14 18 L14 10 M19 18 L19 4")
    val Flame by stroke("M10 2 C15 5 10 8 13 12 C15 12 17 9 17 7 C21 12 22 17 18 20 C14 23 7 22 5 18 C2 12 10 9 10 4 Z")
    val BookmarkFilled by fill("M6 3.5 L18 3.5 L18 21 L12 16.5 L6 21 Z")

    /** Bottom-navigation marks supplied as the product's 24x24 SVG artwork. */
    val ChatNavigation by stroke(
        "M7.40005 6.32015 L15.8901 3.49015 C19.7001 2.22015 21.7701 4.30015 20.5101 8.11015 " +
            "L17.6801 16.6002 C15.7801 22.3102 12.6601 22.3102 10.7601 16.6002 L9.92005 14.0802 " +
            "L7.40005 13.2402 C1.69005 11.3402 1.69005 8.23015 7.40005 6.32015 Z " +
            "M10.11 13.6505 L13.69 10.0605",
        strokeWidth = 1.5f,
    )

    val GroupsNavigation by layeredStroke(
        primaryPath =
            "M9.16006 10.87 C9.06006 10.86 8.94006 10.86 8.83006 10.87 " +
                "C6.45006 10.79 4.56006 8.84 4.56006 6.44 C4.56006 3.99 6.54006 2 9.00006 2 " +
                "C11.4501 2 13.4401 3.99 13.4401 6.44 C13.4301 8.84 11.5401 10.79 9.16006 10.87 Z " +
                "M4.15997 14.56 C1.73997 16.18 1.73997 18.82 4.15997 20.43 " +
                "C6.90997 22.27 11.42 22.27 14.17 20.43 C16.59 18.81 16.59 16.17 14.17 14.56 " +
                "C11.43 12.73 6.91997 12.73 4.15997 14.56 Z",
        secondaryPath =
            "M16.41 4 C18.35 4 19.91 5.57 19.91 7.5 C19.91 9.39 18.41 10.93 16.54 11 " +
                "C16.46 10.99 16.37 10.99 16.28 11 " +
                "M18.34 20 C19.06 19.85 19.74 19.56 20.3 19.13 C21.86 17.96 21.86 16.03 20.3 14.86 " +
                "C19.75 14.44 19.08 14.16 18.37 14",
        strokeWidth = 1.5f,
        secondaryAlpha = 0.4f,
    )

    val ProfileNavigation by stroke(
        "M15.68 3.96 C16.16 4.67 16.44 5.52 16.44 6.44 C16.43 8.84 14.54 10.79 12.16 10.87 " +
            "C12.06 10.86 11.94 10.86 11.83 10.87 C9.61999 10.8 7.82999 9.11 7.58999 6.95 " +
            "C7.29999 4.38 9.40999 2 11.99 2 " +
            "M6.98999 14.56 C4.56999 16.18 4.56999 18.82 6.98999 20.43 " +
            "C9.73999 22.27 14.25 22.27 17 20.43 C19.42 18.81 19.42 16.17 17 14.56 " +
            "C14.27 12.73 9.75999 12.73 6.98999 14.56 Z",
        strokeWidth = 1.5f,
    )

    /**
     * Three dots.
     *
     * Each dot is a ZERO-LENGTH STROKE with a round cap, not a filled circle -- so it takes the
     * set's stroke width automatically and stays exactly as heavy as whatever is beside it. A filled
     * circle would need its own radius and would drift the moment the stroke changed.
     */
    val More by stroke(
        "M11.99 5.5 A0.01 0.01 0 0 1 12.01 5.5 A0.01 0.01 0 0 1 11.99 5.5 " +
            "M11.99 12 A0.01 0.01 0 0 1 12.01 12 A0.01 0.01 0 0 1 11.99 12 " +
            "M11.99 18.5 A0.01 0.01 0 0 1 12.01 18.5 A0.01 0.01 0 0 1 11.99 18.5",
    )

    // -- Message state -------------------------------------------------------------------------

    val Check by stroke("M4.5 12.5 L9.5 17.5 L19.5 6.5")

    /** Two ticks. The read state is the COUNT and never a tint -- see `ChatColors`. */
    val CheckDouble by stroke("M1.5 12.5 L6 17.5 L15 6.5 M9.5 15.5 L11 17.5 L20.5 6.5")

    /** Three compact checked rows: the Tasks bottom-navigation mark. */
    val TasksNavigation by stroke(
        "M3.5 6 L5.5 8 L8.5 4.5 M11 6.5 L20.5 6.5 " +
            "M3.5 12 L5.5 14 L8.5 10.5 M11 12.5 L20.5 12.5 " +
            "M3.5 18 L5.5 20 L8.5 16.5 M11 18.5 L20.5 18.5",
        strokeWidth = 1.5f,
    )

    // -- Actions ---------------------------------------------------------------------------------

    /**
     * The compose action: a square with a pencil crossing its top-right corner.
     *
     * The square is deliberately OPEN at that corner. A closed outline behind the pencil puts two
     * strokes a hair apart there and reads as a smudge at 24dp -- the Canvas version's reason, kept
     * because it was right.
     */
    val Compose by stroke(
        "M18 13.5 L18 19 a1 1 0 0 1 -1 1 L5 20 a1 1 0 0 1 -1 -1 L4 7 a1 1 0 0 1 1 -1 L10.5 6 " +
            "M16.5 3.5 L20.5 7.5 L12 16 L8 16 L8 12 Z",
    )

    val CreateChat by fill(
        "M14.97 22.75 H8.96997 C3.53997 22.75 1.21997 20.43 1.21997 15 V9 " +
            "C1.21997 3.57 3.53997 1.25 8.96997 1.25 H10.97 C11.38 1.25 11.72 1.59 11.72 2 " +
            "C11.72 2.41 11.38 2.75 10.97 2.75 H8.96997 C4.35997 2.75 2.71997 4.39 2.71997 9 V15 " +
            "C2.71997 19.61 4.35997 21.25 8.96997 21.25 H14.97 C19.58 21.25 21.22 19.61 21.22 15 V13 " +
            "C21.22 12.59 21.56 12.25 21.97 12.25 C22.38 12.25 22.72 12.59 22.72 13 V15 " +
            "C22.72 20.43 20.41 22.75 14.97 22.75 Z " +
            "M12.7801 15.2399 C12.6301 15.2399 12.4901 15.1999 12.3601 15.1099 " +
            "C12.1501 14.9699 12.0301 14.7399 12.0301 14.4899 C12.0301 14.3799 12.0201 14.2599 12.0001 14.1399 " +
            "C11.9301 13.6099 11.6901 13.1399 11.2901 12.7299 C10.8801 12.3199 10.3801 12.0599 9.83009 11.9899 " +
            "C9.75009 11.9799 9.62009 11.9699 9.50009 11.9799 C9.25009 11.9999 9.00009 11.8899 8.85009 11.6899 " +
            "C8.70009 11.4899 8.66009 11.2199 8.74009 10.9799 C8.86009 10.6399 9.05009 10.3199 9.27009 10.0699 " +
            "L10.5001 8.52989 C12.6401 5.85989 16.9501 2.65989 20.1301 1.38989 " +
            "C20.8601 1.10989 21.6301 1.26989 22.1601 1.79989 C22.7101 2.34989 22.8701 3.12989 22.5801 3.83989 " +
            "C21.3101 7.00989 18.1101 11.3299 15.4501 13.4599 L13.8801 14.7199 " +
            "C13.5701 14.9399 13.3201 15.0899 13.0601 15.1899 C12.9701 15.2199 12.8701 15.2399 12.7801 15.2399 Z " +
            "M10.7201 10.6699 C11.3301 10.8699 11.8801 11.2099 12.3501 11.6699 " +
            "C12.8101 12.1299 13.1401 12.6599 13.3301 13.2299 L14.5101 12.2799 " +
            "C17.0001 10.2799 20.0001 6.23989 21.1901 3.26989 C21.2701 3.07989 21.1801 2.93989 21.1001 2.84989 " +
            "C21.0501 2.79989 20.9101 2.68989 20.6801 2.77989 C17.7201 3.96989 13.6801 6.96989 11.6801 9.45989 " +
            "L10.7201 10.6699 Z " +
            "M7.69999 18.7502 C7.05999 18.7502 6.44999 18.5002 5.98999 18.0402 " +
            "C5.45999 17.5102 5.19999 16.7802 5.28999 16.0302 L5.49999 14.0702 " +
            "C5.71999 12.0402 7.36999 10.5402 9.43999 10.5002 C9.59999 10.4902 9.79999 10.5002 9.98999 10.5102 " +
            "C10.9 10.6302 11.71 11.0302 12.35 11.6802 C12.99 12.3202 13.38 13.0802 13.49 13.9402 " +
            "C13.52 14.1302 13.53 14.3202 13.53 14.5002 C13.53 15.5902 13.11 16.6202 12.34 17.3902 " +
            "C11.71 18.0202 10.89 18.4102 9.95999 18.5302 L7.97999 18.7402 " +
            "C7.87999 18.7502 7.78999 18.7502 7.69999 18.7502 Z " +
            "M9.59999 11.9902 C9.56999 11.9902 9.52999 11.9902 9.49999 11.9902 " +
            "C8.34999 12.0202 7.13999 12.7902 6.98999 14.2302 L6.77999 16.2002 " +
            "C6.74999 16.4902 6.84999 16.7802 7.04999 16.9802 C7.24999 17.1802 7.52999 17.2802 7.80999 17.2502 " +
            "L9.77999 17.0402 C10.36 16.9702 10.88 16.7202 11.27 16.3302 C11.75 15.8402 12.02 15.2002 12.02 14.5002 " +
            "C12.02 14.3902 12.01 14.2702 11.99 14.1502 C11.92 13.6202 11.68 13.1502 11.28 12.7402 " +
            "C10.87 12.3302 10.37 12.0702 9.81999 12.0002 C9.76999 11.9902 9.68999 11.9902 9.59999 11.9902 Z " +
            "M15.82 12.7295 C15.41 12.7295 15.07 12.3895 15.07 11.9795 " +
            "C15.07 10.3095 13.71 8.93945 12.03 8.93945 C11.62 8.93945 11.28 8.59945 11.28 8.18945 " +
            "C11.28 7.77945 11.62 7.43945 12.03 7.43945 C14.53 7.43945 16.57 9.46945 16.57 11.9795 " +
            "C16.57 12.3895 16.24 12.7295 15.82 12.7295 Z",
    )

    val CreateDiscoverPost by stroke(
        "M16 2 H8 C4 2 2 4 2 8 V21 C2 21.55 2.45 22 3 22 H16 C20 22 22 20 22 16 V8 C22 4 20 2 16 2 Z " +
            "M12.91 7.84015 L7.72004 13.0302 C7.52004 13.2302 7.33004 13.6202 7.29004 13.9002 " +
            "L7.01004 15.8802 C6.91004 16.6002 7.41004 17.1002 8.13004 17.0002 L10.11 16.7202 " +
            "C10.39 16.6802 10.78 16.4902 10.98 16.2902 L16.17 11.1002 " +
            "C17.06 10.2102 17.49 9.17015 16.17 7.85015 C14.85 6.52015 13.81 6.94015 12.91 7.84015 Z " +
            "M12.17 8.58008 C12.61 10.1501 13.84 11.3901 15.42 11.8301",
        strokeWidth = 1.5f,
    )

    val Pencil by stroke("M16.5 3.5 L20.5 7.5 L8 20 L3.5 21 L4.5 16.5 Z M14.5 5.5 L18.5 9.5")

    /** Send. */
    val ArrowUp by stroke("M12 19 L12 5 M5.5 11.5 L12 5 L18.5 11.5")

    val Play by stroke("M8 5 L19 12 L8 19 Z")
    val Pause by stroke("M8 5 L8 19 M16 5 L16 19")

    val Trash by stroke(
        "M4 6.5 L20 6.5 " +
            "M9.5 6.5 L9.5 4.5 a1 1 0 0 1 1 -1 L13.5 3.5 a1 1 0 0 1 1 1 L14.5 6.5 " +
            "M6 6.5 L6.8 19.6 a1 1 0 0 0 1 0.9 L16.2 20.5 a1 1 0 0 0 1 -0.9 L18 6.5",
    )

    val Copy by stroke(
        "M10.5 8.5 L18.5 8.5 A2 2 0 0 1 20.5 10.5 L20.5 18.5 A2 2 0 0 1 18.5 20.5 " +
            "L10.5 20.5 A2 2 0 0 1 8.5 18.5 L8.5 10.5 A2 2 0 0 1 10.5 8.5 Z " +
            "M5.5 15.5 L4.5 15.5 a1 1 0 0 1 -1 -1 L3.5 4.5 a1 1 0 0 1 1 -1 L14.5 3.5 " +
            "a1 1 0 0 1 1 1 L15.5 5.5",
    )

    /** Curved reply arrow used by message actions and swipe-to-reply. */
    val Reply by stroke("M9 6 L3.5 11.5 L9 17 M4 11.5 L13 11.5 C17.5 11.5 20.5 14.5 20.5 19")

    /** Curved forward arrow. Kept separate from Share: this stays inside Swetgram. */
    val Forward by stroke("M15 6 L20.5 11.5 L15 17 M20 11.5 L11 11.5 C6.5 11.5 3.5 14.5 3.5 19")

    /** Opens the reaction picker from surfaces where an emoji row would cover the content. */
    val Reaction by stroke(
        "M12 21 A9 9 0 1 0 12 3 A9 9 0 1 0 12 21 " +
            "M8.5 10 L8.51 10 M15.5 10 L15.51 10 M8.5 14 C10 16 14 16 15.5 14",
    )

    val Refresh by stroke("M20 12 a8 8 0 1 1 -2.34 -5.66 M20.5 3.5 L20.5 8.5 L15.5 8.5")

    val Share by stroke(
        "M12 15.5 L12 3.5 M8 7.5 L12 3.5 L16 7.5 " +
            "M5.5 13 L5.5 19 A1.5 1.5 0 0 0 7 20.5 L17 20.5 A1.5 1.5 0 0 0 18.5 19 L18.5 13",
    )

    val LogOut by stroke(
        "M9.5 20.5 L5.5 20.5 A1.5 1.5 0 0 1 4 19 L4 5 A1.5 1.5 0 0 1 5.5 3.5 L9.5 3.5 " +
            "M15 16.5 L19.5 12 L15 7.5 M19.5 12 L9 12",
    )

    // -- Objects -----------------------------------------------------------------------------

    val Archive by stroke(
        "M4.7 4 L19.3 4 A1.2 1.2 0 0 1 20.5 5.2 L20.5 7.3 A1.2 1.2 0 0 1 19.3 8.5 " +
            "L4.7 8.5 A1.2 1.2 0 0 1 3.5 7.3 L3.5 5.2 A1.2 1.2 0 0 1 4.7 4 Z " +
            "M5.2 8.5 L5.2 18.8 a1.2 1.2 0 0 0 1.2 1.2 L17.6 20 a1.2 1.2 0 0 0 1.2 -1.2 L18.8 8.5 " +
            "M10 12.5 L14 12.5",
    )

    /** Simple folder used by Swetgram Black's inbox benefit. */
    val Folder by stroke(
        "M3.5 6.5 A1.5 1.5 0 0 1 5 5 L10 5 L12 7.5 L19 7.5 " +
            "A1.5 1.5 0 0 1 20.5 9 L20.5 18.5 A1.5 1.5 0 0 1 19 20 L5 20 " +
            "A1.5 1.5 0 0 1 3.5 18.5 Z",
    )

    /** Page outline used by Swetgram Black's storage benefit. */
    val Document by stroke(
        "M6 3.5 L14.5 3.5 L19.5 8.5 L19.5 20.5 L6 20.5 " +
            "A1.5 1.5 0 0 1 4.5 19 L4.5 5 A1.5 1.5 0 0 1 6 3.5 Z " +
            "M14.5 3.5 L14.5 8.5 L19.5 8.5",
    )

    val Bell by stroke(
        "M6 10 a6 6 0 0 1 12 0 C18 15.5 20 17 20 17 L4 17 C4 17 6 15.5 6 10 Z " +
            "M10.2 20.2 a2 2 0 0 0 3.6 0",
    )

    /**
     * The muted mark.
     *
     * THE WHOLE BELL PLUS A SLASH, rather than a bell broken into fragments around the slash. The
     * fragmented version was drawn first and the proof sheet killed it: at 96px the left side of the
     * bell was a stub with a loose terminal, and at 24px it read as damage rather than as a mark.
     */
    val BellOff by stroke(
        "M6 10 a6 6 0 0 1 12 0 C18 15.5 20 17 20 17 L4 17 C4 17 6 15.5 6 10 Z " +
            "M10.2 20.2 a2 2 0 0 0 3.6 0 M4 4 L20 20",
    )

    val Pin by stroke("M9 3 L15 3 M10 3 L10 9.5 L7.5 13 L16.5 13 L14 9.5 L14 3 M12 13 L12 20.5")
    val Bookmark by stroke("M6 3.5 L18 3.5 L18 21 L12 16.5 L6 21 Z")

    val Heart by stroke(
        "M12 20 C12 20 3 14.5 3 8.8 a4.8 4.8 0 0 1 9 -2.2 a4.8 4.8 0 0 1 9 2.2 C21 14.5 12 20 12 20 Z",
    )

    /** The same outline, filled: a liked post. Shape carries the state, never hue. */
    val HeartFilled by fill(
        "M12 20 C12 20 3 14.5 3 8.8 a4.8 4.8 0 0 1 9 -2.2 a4.8 4.8 0 0 1 9 2.2 C21 14.5 12 20 12 20 Z",
    )

    val MessageCircle by stroke(
        "M20.5 11.5 a8.5 8.5 0 0 1 -12.2 7.7 L3.5 20.5 L4.8 15.7 a8.5 8.5 0 1 1 15.7 -4.2 Z",
    )

    val MessageSquare by stroke(
        "M4.5 4 L19.5 4 A1.5 1.5 0 0 1 21 5.5 L21 14.5 A1.5 1.5 0 0 1 19.5 16 " +
            "L9 16 L4.5 20 L4.5 16 A1.5 1.5 0 0 1 3 14.5 L3 5.5 A1.5 1.5 0 0 1 4.5 4 Z",
    )

    val ImageIcon by stroke(
        "M5 3.5 L19 3.5 A1.5 1.5 0 0 1 20.5 5 L20.5 19 A1.5 1.5 0 0 1 19 20.5 " +
            "L5 20.5 A1.5 1.5 0 0 1 3.5 19 L3.5 5 A1.5 1.5 0 0 1 5 3.5 Z " +
            "M7 9.5 A1.6 1.6 0 0 1 10.2 9.5 A1.6 1.6 0 0 1 7 9.5 " +
            // The horizon stops one unit INSIDE the frame at both ends. Run to the frame's own
            // 3.5/20.5 and the 2-wide stroke, being centred, bleeds a unit past it -- so the
            // mountain appears to cut through the picture's border rather than sit in it.
            "M4.8 16.5 L8.5 11.5 L14 17 L16.5 14.5 L19.2 17.2",
    )

    val Link by stroke(
        "M10 13.5 a4.5 4.5 0 0 0 6.8 0.6 L19.5 11.4 a4.5 4.5 0 0 0 -6.4 -6.4 L11.9 6.3 " +
            "M14 10.5 a4.5 4.5 0 0 0 -6.8 -0.6 L4.5 12.6 a4.5 4.5 0 0 0 6.4 6.4 L12.1 17.7",
    )

    val QrCode by stroke(
        "M4 4 L9.5 4 L9.5 9.5 L4 9.5 Z M14.5 4 L20 4 L20 9.5 L14.5 9.5 Z " +
            "M4 14.5 L9.5 14.5 L9.5 20 L4 20 Z " +
            "M14.5 14.5 L16.5 14.5 M20 14.5 L20 16.5 M14.5 18.5 L14.5 20 L16.5 20 M19 20 L20 20",
    )

    val Lock by stroke(
        "M6.5 10.5 L17.5 10.5 A1.5 1.5 0 0 1 19 12 L19 19 A1.5 1.5 0 0 1 17.5 20.5 " +
            "L6.5 20.5 A1.5 1.5 0 0 1 5 19 L5 12 A1.5 1.5 0 0 1 6.5 10.5 Z " +
            "M8 10.5 L8 7.5 a4 4 0 0 1 8 0 L16 10.5",
    )

    val Mail by stroke(
        "M4.5 4.5 L19.5 4.5 A1.5 1.5 0 0 1 21 6 L21 18 A1.5 1.5 0 0 1 19.5 19.5 " +
            "L4.5 19.5 A1.5 1.5 0 0 1 3 18 L3 6 A1.5 1.5 0 0 1 4.5 4.5 Z M3.5 6 L12 12.5 L20.5 6",
    )

    val Eye by stroke(
        "M2 12 C2 12 6 5 12 5 C18 5 22 12 22 12 C22 12 18 19 12 19 C6 19 2 12 2 12 Z " +
            "M12 8.7 A3.3 3.3 0 0 1 12 15.3 A3.3 3.3 0 0 1 12 8.7",
    )

    val EyeOff by stroke(
        "M2 12 C2 12 6 5 12 5 C18 5 22 12 22 12 C22 12 18 19 12 19 C6 19 2 12 2 12 Z " +
            "M12 8.7 A3.3 3.3 0 0 1 12 15.3 A3.3 3.3 0 0 1 12 8.7 M3.5 3.5 L20.5 20.5",
    )

    val Globe by stroke(
        "M12 3.5 A8.5 8.5 0 0 1 12 20.5 A8.5 8.5 0 0 1 12 3.5 M3.5 12 L20.5 12 " +
            "M12 3.5 a13 13 0 0 1 0 17 a13 13 0 0 1 0 -17",
    )

    val Clock by stroke("M12 3.5 A8.5 8.5 0 0 1 12 20.5 A8.5 8.5 0 0 1 12 3.5 M12 7 L12 12 L15.5 14")

    val Calendar by stroke(
        "M3 6 L21 6 M3 6 L3 20 a2 2 0 0 0 2 2 L19 22 a2 2 0 0 0 2 -2 L21 6 M8 3 L8 9 M16 3 L16 9",
    )

    /** Paired footprints for the optional daily walking task. */
    val Steps by stroke(
        "M8.2 3.2 C10.1 3.2 11.1 5.1 10.4 7.1 L9.1 10.7 C8.6 12.1 7.3 12.8 6.1 12.2 " +
            "C4.8 11.5 4.5 9.9 5 8.4 L6.2 4.9 C6.6 3.8 7.2 3.2 8.2 3.2 Z " +
            "M15.8 11.8 C17.7 11.8 18.7 13.7 18 15.7 L16.8 19.2 C16.3 20.7 15 21.3 13.8 20.7 " +
            "C12.5 20 12.2 18.4 12.7 16.9 L13.9 13.5 C14.3 12.4 14.9 11.8 15.8 11.8 Z",
        strokeWidth = 1.5f,
    )

    val MapPin by stroke(
        "M12 2 a8 8 0 0 0 -8 8 c0 5.4 8 12 8 12 s8 -6.6 8 -12 a8 8 0 0 0 -8 -8 Z M12 13 a3 3 0 1 1 0 -6 a3 3 0 0 1 0 6 Z",
    )

    val Music by stroke(
        "M9 18 V5 l12 -2 v13 M9 18 a3 3 0 1 1 -6 0 a3 3 0 0 1 6 0 M21 16 a3 3 0 1 1 -6 0 a3 3 0 0 1 6 0",
    )

    val Mic by stroke(
        "M12 1 a3 3 0 0 0 -3 3 v8 a3 3 0 0 0 6 0 V4 a3 3 0 0 0 -3 -3 Z M19 10 v2 a7 7 0 0 1 -14 0 v-2 M12 19 v4 M8 23 h8",
    )

    // -- People ------------------------------------------------------------------------------

    val User by stroke("M8 8 A4 4 0 0 1 16 8 A4 4 0 0 1 8 8 M4.5 20.5 a7.5 7.5 0 0 1 15 0")

    val Users by stroke(
        "M6 8 A3.5 3.5 0 0 1 13 8 A3.5 3.5 0 0 1 6 8 M3 20.5 a6.5 6.5 0 0 1 13 0 " +
            "M16.5 5 a3.5 3.5 0 0 1 0 6.8 M18 15.2 a6.5 6.5 0 0 1 3 5.3",
    )

    val UserPlus by stroke(
        "M5.5 8 A3.5 3.5 0 0 1 12.5 8 A3.5 3.5 0 0 1 5.5 8 M2.5 20.5 a6.5 6.5 0 0 1 13 0 " +
            "M18.5 8 L18.5 14 M15.5 11 L21.5 11",
    )

    // -- Moderation and status -----------------------------------------------------------------

    val Flag by stroke("M5 21 L5 4 M5 4.5 L18.5 4.5 L15.5 9 L18.5 13.5 L5 13.5")

    val Shield by stroke("M12 3 L20 6 L20 11.5 C20 16.5 16.5 19.8 12 21 C7.5 19.8 4 16.5 4 11.5 L4 6 Z")

    val Ban by stroke("M12 3.5 A8.5 8.5 0 0 1 12 20.5 A8.5 8.5 0 0 1 12 3.5 M6 6 L18 18")

    val AlertCircle by stroke(
        "M12 3.5 A8.5 8.5 0 0 1 12 20.5 A8.5 8.5 0 0 1 12 3.5 M12 7.5 L12 13 " +
            "M11.99 16.5 A0.01 0.01 0 0 1 12.01 16.5 A0.01 0.01 0 0 1 11.99 16.5",
    )

    val Info by stroke(
        "M12 3.5 A8.5 8.5 0 0 1 12 20.5 A8.5 8.5 0 0 1 12 3.5 M12 16.5 L12 11 " +
            "M11.99 7.5 A0.01 0.01 0 0 1 12.01 7.5 A0.01 0.01 0 0 1 11.99 7.5",
    )

    /**
     * Settings: three rails with knobs, NOT a gear.
     *
     * A gear was drawn first and abandoned. A twelve-arc cog is the one shape in this vocabulary
     * that cannot survive being scaled to 20dp -- the teeth close up into a ring and the whole mark
     * turns into a blurred circle, which is exactly the failure the old Canvas set was full of.
     * Three rails read at every size in this app.
     */
    val Sliders by stroke(
        "M3.5 7 L12 7 M17 7 L20.5 7 M12 7 A2.5 2.5 0 0 1 17 7 A2.5 2.5 0 0 1 12 7 " +
            "M3.5 12 L7 12 M12 12 L20.5 12 M7 12 A2.5 2.5 0 0 1 12 12 A2.5 2.5 0 0 1 7 12 " +
            "M3.5 17 L14 17 M19 17 L20.5 17 M14 17 A2.5 2.5 0 0 1 19 17 A2.5 2.5 0 0 1 14 17",
    )

    // -- Built lazily, so an icon nothing draws costs nothing at startup -------------------------

    private fun stroke(pathData: String, strokeWidth: Float = SwetIconGrid.STROKE) =
        LazyIcon(pathData, filled = false, strokeWidth = strokeWidth)

    private fun fill(pathData: String) = LazyIcon(pathData, filled = true)

    private fun layeredStroke(
        primaryPath: String,
        secondaryPath: String,
        strokeWidth: Float,
        secondaryAlpha: Float,
    ) = LazyLayeredStrokeIcon(primaryPath, secondaryPath, strokeWidth, secondaryAlpha)
}

/**
 * The 24-grid, the 2.0 stroke and the round terminals, applied in one place.
 *
 * A property delegate rather than a function so each icon is parsed and built ONCE, on first draw.
 * `ImageVector` is immutable with a structural `equals`, so a cached instance is safe to share
 * across compositions and across screens.
 */
internal class LazyIcon(
    private val pathData: String,
    private val filled: Boolean,
    private val strokeWidth: Float = SwetIconGrid.STROKE,
) {

    private var cached: ImageVector? = null

    operator fun getValue(thisRef: Any?, property: KProperty<*>): ImageVector =
        cached ?: build(property.name).also { cached = it }

    private fun build(name: String): ImageVector =
        ImageVector.Builder(
            name = "SwetIcons.$name",
            defaultWidth = SwetIconGrid.dp,
            defaultHeight = SwetIconGrid.dp,
            viewportWidth = SwetIconGrid.VIEWPORT,
            viewportHeight = SwetIconGrid.VIEWPORT,
        ).apply {
            val nodes = addPathNodes(pathData)
            if (filled) {
                addPath(pathData = nodes, fill = SolidColor(Color.Black))
            } else {
                addPath(
                    pathData = nodes,
                    fill = null,
                    // Never seen -- `SwetIcon` draws through a tint filter. It still has to be an
                    // opaque colour rather than null, or the path has neither a fill nor a stroke
                    // and renders as nothing at all.
                    stroke = SolidColor(Color.Black),
                    strokeLineWidth = strokeWidth,
                    strokeLineCap = StrokeCap.Round,
                    strokeLineJoin = StrokeJoin.Round,
                )
            }
        }.build()
}

/** Two-tone SVG stroke artwork, used when the source intentionally de-emphasises one layer. */
internal class LazyLayeredStrokeIcon(
    private val primaryPath: String,
    private val secondaryPath: String,
    private val strokeWidth: Float,
    private val secondaryAlpha: Float,
) {
    private var cached: ImageVector? = null

    operator fun getValue(thisRef: Any?, property: KProperty<*>): ImageVector =
        cached ?: build(property.name).also { cached = it }

    private fun build(name: String): ImageVector =
        ImageVector.Builder(
            name = "SwetIcons.$name",
            defaultWidth = SwetIconGrid.dp,
            defaultHeight = SwetIconGrid.dp,
            viewportWidth = SwetIconGrid.VIEWPORT,
            viewportHeight = SwetIconGrid.VIEWPORT,
        ).apply {
            addStrokeLayer(primaryPath, alpha = 1f)
            addStrokeLayer(secondaryPath, alpha = secondaryAlpha)
        }.build()

    private fun ImageVector.Builder.addStrokeLayer(pathData: String, alpha: Float) {
        addPath(
            pathData = addPathNodes(pathData),
            fill = null,
            stroke = SolidColor(Color.Black),
            strokeAlpha = alpha,
            strokeLineWidth = strokeWidth,
            strokeLineCap = StrokeCap.Round,
            strokeLineJoin = StrokeJoin.Round,
        )
    }
}

/**
 * The set's constants, named rather than repeated.
 *
 * [VIEWPORT] and [dp] are the SAME number on purpose: a 24-unit viewport declared as 24dp makes one
 * viewport unit one dp at the natural size, so the 2.0 stroke reads as 2dp and every coordinate in
 * the set can be read straight off a 24dp mock.
 */
internal object SwetIconGrid {
    const val VIEWPORT: Float = 24f
    const val STROKE: Float = 2f
    val dp: Dp = 24.dp
}

/**
 * One icon, tinted by the caller.
 *
 * [contentDescription] is deliberately non-optional-to-think-about, exactly as `SwetGlyphIcon`'s
 * was: pass a description for a mark that carries meaning on its own, and `null` for one that does
 * not. Null hides it from the accessibility tree, which is right whenever a label sits beside it or
 * a button wraps it -- otherwise a screen reader says the same word twice.
 *
 * `androidx.compose.material3.Icon` is deliberately NOT used. It falls back to `LocalContentColor`
 * when the tint is unspecified, and this app has no content-colour discipline to lean on: every call
 * site names its colour from the tokens, and a component that silently substitutes Material's idea
 * of a foreground is how an off-token colour gets on screen without anybody deciding.
 */
@Composable
internal fun SwetIcon(
    icon: ImageVector,
    tint: Color,
    contentDescription: String?,
    modifier: Modifier = Modifier,
    size: Dp = Sizes.iconMedium,
) {
    // Aliased before the lambda: inside `semantics { }` the receiver has a property of the same
    // name, and a reader should not have to work out which one the right-hand side means.
    val description = contentDescription
    val semanticsModifier =
        if (description != null) {
            Modifier.semantics { this.contentDescription = description }
        } else {
            Modifier.clearAndSetSemantics { }
        }

    Image(
        imageVector = icon,
        contentDescription = null,
        modifier = modifier.size(size).then(semanticsModifier),
        colorFilter = ColorFilter.tint(tint),
    )
}
